import { motion, useScroll, useTransform } from 'motion/react';
import { Award, Globe, Zap, Target, Users, TrendingUp, Lightbulb, Rocket, Shield } from 'lucide-react';
import { useRef, useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";

const TEAM = [
  {
    name: 'Rajiv',
    role: 'Founder & CEO',
  },
  {
    name: 'Selvakumar',
    role: 'Co-Founder & MD',
  },
  {
    name: 'Aishwarya',
    role: 'Digital Marketing Executive',
  },
  {
    name: 'Gowtham',
    role: 'Business Development Executive',
  },
  {
    name: 'Gurumoorthi',
    role: 'Insurance Executive',
  },
  {
    name: 'Jagatheesan',
    role: 'Customer Service Executive'
  },
];

// Images matched to each content section
const STORY_SECTIONS = [
  {
    id: 'story',
    label: 'The Stone Bench Story',
    image: '././about.jpg',
    imageAlt: 'Stone Bench founding story — local community roots',
    content: (
      <p className="text-[var(--color)] leading-relaxed">
        What started as a local initiative in Udumalpet over 8 years ago has steadily grown — first to Pollachi, and now proudly serving clients in Coimbatore. Our journey has been driven by consistency, integrity, and word-of-mouth trust.
      </p>
    ),
  },
  {
    id: 'mission',
    label: 'Our Mission',
    image: '././our_mission.webp',
    imageAlt: 'Our mission — empowering families with financial strategies',
    content: (
      <p className="text-[var(--color)] leading-relaxed">
        Empowering families with research-backed strategies and honest guidance for a secure financial future.
      </p>
    ),
  },
  {
    id: 'quote',
    label: 'Our Promise',
    image: '././our_promises.webp',
    imageAlt: 'Our promise to help you grow with confidence',
    content: (
      <p className="text-[var(--color)] leading-relaxed italic border-l-2 border-[var(--color-accent)] pl-4">
        "Whether you're just starting or looking for reliable long-term guidance — Stone Bench is here to help you grow with confidence."
      </p>
    ),
  },
];

export default function About() {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const [activeSection, setActiveSection] = useState('story');
  const current = STORY_SECTIONS.find(s => s.id === activeSection) ?? STORY_SECTIONS[0];
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Team slider functions
  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % TEAM.length);
  };
  
  // Auto-play for team slider
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        setIsPlaying(false);
      });
    }
  }, []);

  const toggleVideo = () => {
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  return (
    <div className="overflow-x-hidden">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-16 sm:space-y-24">

        {/* Hero - Premium Roadmap Timeline */}
        <section className="mx-auto relative">
          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 left-10 w-32 h-32 bg-[var(--color-accent)] opacity-5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-40 h-40 bg-[var(--color-blue)] opacity-5 rounded-full blur-3xl"></div>
          </div>

          {/* Hero Header */}
          <motion.div className="hero-invesment relative w-full min-h-[100svh] overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-black">
            <div className="grid grid-cols-1 md:grid-cols-2 min-h-[100svh]">
              {/* Left Side - Video */}
              <div className="relative h-[50vw] min-h-[280px] md:h-full flex items-center justify-center bg-black/30">
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover"
                >
                  <source src="/./hero-video-hd.mp4" type="video/mp4" />
                </video>
                {/* Video Control Button */}
                <div className="play-btn absolute left-6 top-6 z-10">
                  <button
                    onClick={toggleVideo}
                    className="group relative w-14 h-14 rounded-full flex items-center justify-center bg-white/10 backdrop-blur-xl border border-white/20 shadow-[0_10px_40px_rgba(0,0,0,0.4)] hover:scale-110 transition-all duration-300"
                  >
                    <span className="absolute inset-0 rounded-full bg-white/20 animate-ping"></span>
                    <span className="relative text-white">
                      {isPlaying ? <Pause size={22} /> : <Play size={22} />}
                    </span>
                  </button>
                </div>
              </div>

              {/* Right Side - About Content */}
              <div className="h-full flex items-center justify-center px-6 sm:px-8 lg:px-12 bg-gradient-to-r from-black/50 via-transparent to-black/30 py-10 md:py-0">
                <motion.div 
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="max-w-xl text-center lg:text-left"
                >
                  {/* Premium Badge */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="badge inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[var(--color-accent)]/20 to-[var(--color-blue)]/20 border border-white/10 backdrop-blur-xl mb-8"
                  >
                    <div className="w-2 h-2 rounded-full bg-[var(--color-accent)] animate-pulse" />
                    <span className="text-xs font-bold uppercase tracking-widest text-white/90">Since 2017</span>
                  </motion.div>

                  {/* Main Heading with Animation */}
                  <motion.div className="space-y-4">
                    <motion.span 
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.5 }}
                      className="block text-xl sm:text-2xl font-light text-white/90"
                    >
                      Journey of
                    </motion.span>
                    <motion.h2 
                      initial={{ opacity: 0, y: 40, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 1, delay: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
                      className="block text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-8 bg-gradient-to-r from-white via-white/90 to-white/80 bg-clip-text text-transparent drop-shadow-2xl"
                    >
                      STONEBENCH
                      <motion.span 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 1.2 }}
                        className="block text-3xl lg:text-4xl font-bold bg-gradient-to-r from-[var(--color-accent)] via-[var(--color-blue)] to-[var(--color-accent)] bg-clip-text text-transparent"
                      >
                        INVESTMENTS
                      </motion.span>
                    </motion.h2>
                    
                    {/* Description Text with Staggered Animation */}
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.8, delay: 1.5 }}
                      className="space-y-6 text-white/95"
                    >
                      <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.7 }}
                        className="text-lg leading-relaxed"
                      >
                        Empowering individuals with practical trading and investment knowledge to build confident, sustainable wealth.
                      </motion.p>
                      <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.9 }}
                        className="text-lg leading-relaxed"
                      >
                        Guiding every learner beyond education into real-world financial success through mentorship and smart market strategies.
                      </motion.p>
                    </motion.div>

                    {/* Animated Stats/Features */}
                    <motion.div 
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 2.1 }}
                      className="grid grid-cols-3 gap-4 mt-8"
                    >
                      {[
                        { 
                          icon: (
                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                          ), 
                          label: 'Clients', 
                          value: '1000+' 
                        },
                        { 
                          icon: (
                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                          ), 
                          label: 'Experience', 
                          value: '10+ Years' 
                        },                        
                        { 
                          icon: (
                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          ), 
                          label: 'Success', 
                          value: '98%' 
                        }
                      ].map((stat, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.5, delay: 2.3 + (i * 0.1) }}
                          whileHover={{ y: -5, scale: 1.05 }}
                          className="text-center p-3 rounded-xl bg-[#1919707d]  border border-white/10"
                        >
                          <div className="text-2xl mb-1 text-[var(--color-accent)]">{stat.icon}</div>
                          <div className="text-xs font-bold text-white/80">{stat.label}</div>
                          <div className="text-sm font-black text-[var(--color-accent)]">{stat.value}</div>
                        </motion.div>
                      ))}
                    </motion.div>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Premium Timeline */}
          <div className="relative bg-gradient-to-br from-[#0f172a] via-[#111827] to-[#020617] p-[30px] rounded-[20px] mt-10">
            {/* Enhanced Timeline Line with glow effect */}
            <div className="absolute left-1/2 -translate-x-1/2 w-[2px] h-full bg-gradient-to-b from-green-400 via-blue-500 to-purple-500 shadow-[0_0_20px_rgba(34,197,94,0.6)]"></div>

            {/* Advanced Animated Background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {/* Dynamic gradient background */}
              <motion.div
                animate={{
                  background: [
                    "linear-gradient(135deg, rgba(95,156,76,0.02) 0%, rgba(29,95,168,0.02) 50%, rgba(139,92,246,0.02) 100%)",
                    "linear-gradient(225deg, rgba(29,95,168,0.02) 0%, rgba(139,92,246,0.02) 50%, rgba(95,156,76,0.02) 100%)",
                    "linear-gradient(315deg, rgba(139,92,246,0.02) 0%, rgba(95,156,76,0.02) 50%, rgba(29,95,168,0.02) 100%)",
                    "linear-gradient(45deg, rgba(95,156,76,0.02) 0%, rgba(29,95,168,0.02) 50%, rgba(139,92,246,0.02) 100%)"
                  ]
                }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0"
              />

              {/* Floating geometric shapes */}
              <motion.div
                animate={{
                  rotate: [0, 360],
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.1, 0.3]
                }}
                transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-20 left-20 w-24 h-24 border-4 border-[var(--color-accent)] border-opacity-20 rotate-45"
              />
              <motion.div
                animate={{
                  rotate: [360, 0],
                  scale: [1, 0.8, 1],
                  opacity: [0.2, 0.05, 0.2]
                }}
                transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
                className="absolute bottom-20 right-20 w-32 h-32 border-4 border-[var(--color-blue)] border-opacity-20 rounded-full"
              />

              {/* Morphing blobs */}
              <motion.div
                animate={{
                  borderRadius: ["20%", "50%", "30%", "50%", "20%"],
                  rotate: [0, 180, 360],
                  scale: [1, 1.3, 0.9, 1.3, 1]
                }}
                transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-1/4 left-1/4 w-20 h-20 bg-gradient-to-r from-[var(--color-accent)] to-purple-500 opacity-20 blur-xl"
              />
              <motion.div
                animate={{
                  borderRadius: ["50%", "20%", "40%", "20%", "50%"],
                  rotate: [360, 180, 0],
                  scale: [0.8, 1.2, 0.9, 1.2, 0.8]
                }}
                transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
                className="absolute bottom-1/4 right-1/4 w-24 h-24 bg-gradient-to-r from-[var(--color-blue)] to-pink-500 opacity-15 blur-xl"
              />

              {/* Particle field with complex movements */}
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  animate={{
                    x: [0, Math.random() * 200 - 100, 0],
                    y: [0, Math.random() * 200 - 100, 0],
                    scale: [1, Math.random() * 0.5 + 0.8, 1],
                    opacity: [0.4, 0.1, 0.4]
                  }}
                  transition={{
                    duration: Math.random() * 5 + 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.5
                  }}
                  className={`absolute w-2 h-2 rounded-full ${i % 3 === 0 ? 'bg-[var(--color-accent)]' :
                    i % 3 === 1 ? 'bg-[var(--color-blue)]' : 'bg-purple-500'
                    }`}
                  style={{
                    top: `${Math.random() * 80 + 10}%`,
                    left: `${Math.random() * 80 + 10}%`
                  }}
                />
              ))}

              {/* Energy waves */}
              <motion.div
                animate={{
                  scale: [1, 3, 1],
                  opacity: [0.2, 0, 0.2]
                }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeOut" }}
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 border-2 border-[var(--color-accent)] rounded-full"
              />
              <motion.div
                animate={{
                  scale: [1, 2.5, 1],
                  opacity: [0.15, 0, 0.15]
                }}
                transition={{ duration: 10, repeat: Infinity, ease: "easeOut", delay: 2 }}
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 border-2 border-[var(--color-blue)] rounded-full"
              />

              {/* Floating light beams */}
              <motion.div
                animate={{
                  rotate: [45, 135, 45],
                  opacity: [0.1, 0.3, 0.1]
                }}
                transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-10 right-1/3 w-1 h-32 bg-gradient-to-b from-transparent via-yellow-400 to-transparent opacity-20"
              />
              <motion.div
                animate={{
                  rotate: [-45, -135, -45],
                  opacity: [0.15, 0.25, 0.15]
                }}
                transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
                className="absolute bottom-10 left-1/3 w-1 h-40 bg-gradient-to-t from-transparent via-cyan-400 to-transparent opacity-20"
              />
            </div>

            <div className="space-y-16 relative z-10">
              {/* 2019 */}
              <motion.div
                initial={{ opacity: 0, x: -100, scale: 0.9 }}
                whileInView={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true, margin: "-100px" }}
                className="flex flex-col lg:flex-row gap-8 items-center relative"
              >
                {/* Timeline Node */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-[var(--color-accent)] rounded-full border-4 border-white shadow-lg z-20">
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    className="w-full h-full bg-[var(--color-accent)] rounded-full"
                  />
                </div>

                <div className="lg:w-5/12 lg:text-right">
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    viewport={{ once: true }}
                  >
                    <h2 className="text-4xl font-black text-[var(--color-accent)] mb-2">2019</h2>
                    <div className="w-20 h-1 bg-[var(--color-accent)] ml-auto"></div>
                  </motion.div>
                </div>
                <div className="lg:w-1/12"></div>
                <div className="lg:w-5/12">
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.02, y: -5 }}
                    className="rounded-3xl p-8 bg-white/5 backdrop-blur-xl border border-white/10
                      shadow-[0_20px_60px_rgba(0,0,0,0.4)]
                      hover:shadow-[0_30px_80px_rgba(0,0,0,0.6)]
                      hover:-translate-y-2 transition-all duration-500"                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-accent)] to-transparent opacity-0 group-hover:opacity-5 transition-opacity duration-500"></div>
                    <div className="relative z-10">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 rounded-full bg-[var(--color-accent)] bg-opacity-20 flex items-center justify-center mr-4">
                          <Rocket className="w-6 h-6 text-[#fff]" />
                        </div>
                        <motion.h3
                          initial={{ opacity: 0, x: 50 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.8, delay: 0.3 }}
                          className="text-2xl font-bold text-[#fff]"
                        >
                          <motion.span
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="inline-block"
                          >
                            The
                          </motion.span>
                          <motion.span
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="inline-block"
                          >
                            Foundation
                          </motion.span>
                        </motion.h3>
                      </div>
                      <p className="text-[#fff] leading-relaxed">
                        Stone Bench Investments was founded by Mr. Rajiv and Mr. Selvakumar with a vision to empower individuals with practical knowledge about trading and investing.
                      </p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              {/* 2020 */}
              <motion.div
                initial={{ opacity: 0, x: 100, scale: 0.9 }}
                whileInView={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true, margin: "-100px" }}
                className="flex flex-col lg:flex-row gap-8 items-center relative"
              >
                {/* Timeline Node */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-[var(--color-accent)] rounded-full border-4 border-white shadow-lg z-20">
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    className="w-full h-full bg-[var(--color-accent)] rounded-full"
                  />
                </div>

                <div className="lg:w-5/12 lg:text-right order-2 lg:order-1">
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.02, y: -5 }}
                    className="rounded-3xl p-8 bg-white/5 backdrop-blur-xl border border-white/10
                      shadow-[0_20px_60px_rgba(0,0,0,0.4)]
                      hover:shadow-[0_30px_80px_rgba(0,0,0,0.6)]
                      hover:-translate-y-2 transition-all duration-500"                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-blue)] to-transparent opacity-0 group-hover:opacity-5 transition-opacity duration-500"></div>
                    <div className="relative z-10">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 rounded-full bg-[#1d5fa8] bg-opacity-20 flex items-center justify-center mr-4">
                          <Users className="w-6 h-6 text-[#fff]" />
                        </div>
                        <motion.h3
                          initial={{ opacity: 0, x: 50 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.8, delay: 0.3 }}
                          className="text-2xl font-bold text-[#fff]"
                        >
                          <motion.span
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="inline-block"
                          >
                            Growth &
                          </motion.span>
                          <motion.span
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="inline-block"
                          >
                            Expansion
                          </motion.span>
                        </motion.h3>
                      </div>
                      <p className="text-[#fff] leading-relaxed">
                        The team expanded to include dedicated market analysts and support staff. We began offering comprehensive trading courses and investment solutions.
                      </p>
                    </div>
                  </motion.div>
                </div>
                <div className="lg:w-1/12 order-1 lg:order-2"></div>
                <div className="lg:w-5/12 lg:text-left order-3">
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    viewport={{ once: true }}
                  >
                    <h2 className="text-4xl font-black text-[var(--color-accent)] mb-2">2020</h2>
                    <div className="w-20 h-1 bg-[var(--color-accent)]"></div>
                  </motion.div>
                </div>
              </motion.div>

              {/* 2021 */}
              <motion.div
                initial={{ opacity: 0, x: -100, scale: 0.9 }}
                whileInView={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true, margin: "-100px" }}
                className="flex flex-col lg:flex-row gap-8 items-center relative"
              >
                {/* Timeline Node */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-[var(--color-accent)] rounded-full border-4 border-white shadow-lg z-20">
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    className="w-full h-full bg-[var(--color-accent)] rounded-full"
                  />
                </div>

                <div className="lg:w-5/12 lg:text-right">
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    viewport={{ once: true }}
                  >
                    <h2 className="text-4xl font-black text-[var(--color-accent)] mb-2">2021</h2>
                    <div className="w-20 h-1 bg-[var(--color-accent)] ml-auto"></div>
                  </motion.div>
                </div>
                <div className="lg:w-1/12"></div>
                <div className="lg:w-5/12">
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.02, y: -5 }}
                    className="rounded-3xl p-8 bg-white/5 backdrop-blur-xl border border-white/10
                      shadow-[0_20px_60px_rgba(0,0,0,0.4)]
                      hover:shadow-[0_30px_80px_rgba(0,0,0,0.6)]
                      hover:-translate-y-2 transition-all duration-500"                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-transparent opacity-0 group-hover:opacity-5 transition-opacity duration-500"></div>
                    <div className="relative z-10">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 rounded-full bg-purple-500 bg-opacity-20 flex items-center justify-center mr-4">
                          <Target className="w-6 h-6 text-[#fff]" />
                        </div>
                        <motion.h3
                          initial={{ opacity: 0, x: 50 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.8, delay: 0.3 }}
                          className="text-2xl font-bold text-[#fff]"
                        >
                          <motion.span
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="inline-block"
                          >
                            Service
                          </motion.span>
                          <motion.span
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="inline-block"
                          >
                            Diversification
                          </motion.span>
                        </motion.h3>
                      </div>
                      <p className="text-[#fff] leading-relaxed">
                        Expanded our offerings to include personalized portfolio management, advanced trading strategies, and real-time market advisory services.
                      </p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              {/* 2022 */}
              <motion.div
                initial={{ opacity: 0, x: 100, scale: 0.9 }}
                whileInView={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true, margin: "-100px" }}
                className="flex flex-col lg:flex-row gap-8 items-center relative"
              >
                {/* Timeline Node */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-[var(--color-accent)] rounded-full border-4 border-white shadow-lg z-20">
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    className="w-full h-full bg-[var(--color-accent)] rounded-full"
                  />
                </div>

                <div className="lg:w-5/12 lg:text-right order-2 lg:order-1">
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.02, y: -5 }}
                    className="rounded-3xl p-8 bg-white/5 backdrop-blur-xl border border-white/10
                      shadow-[0_20px_60px_rgba(0,0,0,0.4)]
                      hover:shadow-[0_30px_80px_rgba(0,0,0,0.6)]
                      hover:-translate-y-2 transition-all duration-500"                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-transparent opacity-0 group-hover:opacity-5 transition-opacity duration-500"></div>
                    <div className="relative z-10">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 rounded-full bg-orange-500 bg-opacity-20 flex items-center justify-center mr-4">
                          <Lightbulb className="w-6 h-6 text-[#fff]" />
                        </div>
                        <motion.h3
                          initial={{ opacity: 0, x: 50 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.8, delay: 0.3 }}
                          className="text-2xl font-bold text-[#fff]"
                        >
                          <motion.span
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="inline-block"
                          >
                            Technology
                          </motion.span>
                          <motion.span
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="inline-block"
                          >
                            Integration
                          </motion.span>
                        </motion.h3>
                      </div>
                      <p className="text-[#fff] leading-relaxed">
                        Launched digital platforms and tools to provide seamless trading experiences. Implemented advanced analytics and AI-driven insights.
                      </p>
                    </div>
                  </motion.div>
                </div>
                <div className="lg:w-1/12 order-1 lg:order-2"></div>
                <div className="lg:w-5/12 lg:text-left order-3">
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    viewport={{ once: true }}
                  >
                    <h2 className="text-4xl font-black text-[var(--color-accent)] mb-2">2022</h2>
                    <div className="w-20 h-1 bg-[var(--color-accent)]"></div>
                  </motion.div>
                </div>
              </motion.div>

              {/* 2023 */}
              <motion.div
                initial={{ opacity: 0, x: -100, scale: 0.9 }}
                whileInView={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true, margin: "-100px" }}
                className="flex flex-col lg:flex-row gap-8 items-center relative"
              >
                {/* Timeline Node */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-[var(--color-accent)] rounded-full border-4 border-white shadow-lg z-20">
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    className="w-full h-full bg-[var(--color-accent)] rounded-full"
                  />
                </div>

                <div className="lg:w-5/12 lg:text-right">
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    viewport={{ once: true }}
                  >
                    <h2 className="text-4xl font-black text-[var(--color-accent)] mb-2">2023</h2>
                    <div className="w-20 h-1 bg-[var(--color-accent)] ml-auto"></div>
                  </motion.div>
                </div>
                <div className="lg:w-1/12"></div>
                <div className="lg:w-5/12">
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.02, y: -5 }}
                    className="rounded-3xl p-8 bg-white/5 backdrop-blur-xl border border-white/10
                      shadow-[0_20px_60px_rgba(0,0,0,0.4)]
                      hover:shadow-[0_30px_80px_rgba(0,0,0,0.6)]
                      hover:-translate-y-2 transition-all duration-500"                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-pink-500 to-transparent opacity-0 group-hover:opacity-5 transition-opacity duration-500"></div>
                    <div className="relative z-10">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 rounded-full bg-pink-500 bg-opacity-20 flex items-center justify-center mr-4">
                          <TrendingUp className="w-6 h-6 text-[#fff]" />
                        </div>
                        <motion.h3
                          initial={{ opacity: 0, x: 50 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.8, delay: 0.3 }}
                          className="text-2xl font-bold text-[#fff]"
                        >
                          <motion.span
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="inline-block"
                          >
                            Market
                          </motion.span>
                          <motion.span
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="inline-block"
                          >
                            Leadership
                          </motion.span>
                        </motion.h3>
                      </div>
                      <p className="text-[#fff] leading-relaxed">
                        Established ourselves as a trusted financial partner across Tamil Nadu. Successfully guided thousands of clients through various market cycles.
                      </p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              {/* 2024 */}
              <motion.div
                initial={{ opacity: 0, x: 100, scale: 0.9 }}
                whileInView={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true, margin: "-100px" }}
                className="flex flex-col lg:flex-row gap-8 items-center relative"
              >
                {/* Timeline Node */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-gradient-to-r from-[var(--color-accent)] to-[var(--color-blue)] rounded-full border-4 border-white shadow-lg z-20">
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    className="w-full h-full bg-gradient-to-r from-[var(--color-accent)] to-[var(--color-blue)] rounded-full"
                  />
                </div>

                <div className="lg:w-5/12 lg:text-right order-2 lg:order-1">
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.02, y: -5 }}
                    className="rounded-3xl p-8 bg-white/5 backdrop-blur-xl border border-white/10
shadow-[0_20px_60px_rgba(0,0,0,0.4)]
hover:shadow-[0_30px_80px_rgba(0,0,0,0.6)]
hover:-translate-y-2 transition-all duration-500"                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-accent)] to-[var(--color-blue)] opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>
                    <div className="relative z-10">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[var(--color-accent)] to-[var(--color-blue)] bg-opacity-10 flex items-center justify-center mr-4">
                          <Shield className="w-6 h-6 text-white bg-gradient-to-r from-[var(--color-accent)] to-[var(--color-blue)]" />
                        </div>
                        <motion.h3
                          initial={{ opacity: 0, x: 50 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.8, delay: 0.3 }}
                          className="text-2xl font-bold text-[#fff]"
                        >
                          <motion.span
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="inline-block"
                          >
                            Excellence &
                          </motion.span>
                          <motion.span
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="inline-block"
                          >
                            Innovation
                          </motion.span>
                        </motion.h3>
                      </div>
                      <p className="text-[#fff] leading-relaxed">
                        Today, Stone Bench Investments stands as a leading financial education and investment platform. With thousands of successful clients and a proven track record.
                      </p>
                    </div>
                  </motion.div>
                </div>
                <div className="lg:w-1/12 order-1 lg:order-2"></div>
                <div className="lg:w-5/12 lg:text-left order-3">
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    viewport={{ once: true }}
                  >
                    <h2 className="text-4xl font-black bg-gradient-to-r from-[var(--color-accent)] to-[var(--color-blue)] bg-clip-text text-transparent mb-2">2024</h2>
                    <div className="w-20 h-1 bg-gradient-to-r from-[var(--color-accent)] to-[var(--color-blue)]"></div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-12 sm:gap-x-8 sm:gap-y-14">
          {[
            { icon: Award, color: 'var(--color-accent)', title: 'Local Expertise', desc: 'Deep roots in your region with strong understanding of local financial needs.' },
            { icon: Globe, color: 'var(--color-blue)', title: 'Client-First Approach', desc: 'Personalized service, open communication, and honest advice every step of the way.' },
            { icon: Zap, color: '#d97706', title: 'Sustainable Growth', desc: 'Strategies built for long-term success, not short-term hype.' },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="relative pt-7 group">
                {/* Icon badge — centered at top, overlapping the card edge */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform duration-500 group-hover:[transform:rotateY(360deg)]"
                    style={{ background: item.color }}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                {/* Card body */}
                <div className="rounded-2xl sm:rounded-3xl pt-12 pb-6 px-6 sm:px-8 sm:pb-8 text-center
bg-white/5 backdrop-blur-xl border border-white/10
hover:border-[var(--color-accent)] transition-all duration-500">
                  <h3 className="text-lg font-bold mb-3 text-[var(--color-text)]">{item.title}</h3>
                  <p className="text-sm text-[var(--color-muted)] leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Story section — image changes based on active content tab */}
        <section className="rounded-2xl sm:rounded-[3rem] bg-white border border-[#e1ff01] p-6 sm:p-12 lg:p-16 relative overflow-hidden shadow-sm w-[90%] mx-auto ">
          <motion.div style={{ y, background: 'rgba(34,197,94,0.1)' }}
            className="absolute top-0 right-0 w-80 h-80 rounded-full blur-[120px]" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

            {/* Left — tab selector + content */}
            <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>

              {/* Section tabs */}
              <div className="flex flex-wrap gap-2 mb-7">
                {STORY_SECTIONS.map(s => (
                  <button
                    key={s.id}
                    onClick={() => setActiveSection(s.id)}
                    className={`px-4 py-1.5 rounded-full text-base font-bold transition-all ${activeSection === s.id
                      ? 'text-white shadow-md'
                      : 'text-[var(--color-muted)] hover:text-[var(--color-text)]'
                      }`}
                    style={activeSection === s.id
                      ? { background: 'var(--color-accent)' }
                      : { background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }
                    }
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Active section title */}
              <motion.h2
                key={activeSection + '-title'}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="text-[clamp(1.5rem,3.5vw,2.5rem)] font-black mb-5 text-[var(--color-text)] tracking-tight"
              >
                {current.label}
              </motion.h2>

              {/* Active section content */}
              <motion.div
                key={activeSection + '-content'}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="mb-7"
              >
                {current.content}
              </motion.div>

              {/* Mission pill — always visible */}
              <div className="flex items-start gap-4 p-5 rounded-2xl bg-white/60 border border-[rgba(34,197,94,0.15)] shadow-sm backdrop-blur-sm">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(34,197,94,0.15)' }}>
                  <Target className="w-5 h-5" style={{ color: '#16a34a' }} />
                </div>
                <div>
                  <p className="font-bold text-[var(--color-text)] text-sm mb-1">8+ Years · 3 Cities · Thousands of Clients</p>
                  <p className="text-xs text-[#5fc66f] leading-relaxed">
                    Udumalpet → Pollachi → Coimbatore. Every client a partner, every goal our commitment.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right — image changes with active section */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="aspect-square rounded-2xl sm:rounded-3xl overflow-hidden glass p-2 sticky top-28"
            >
              <motion.img
                key={current.image}
                src={current.image}
                alt={current.imageAlt}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.45, ease: 'easeOut' }}
                className="w-full h-full object-cover rounded-xl sm:rounded-2xl"
              />
              {/* Caption */}
              <motion.div
                key={current.id + '-cap'}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="absolute bottom-4 left-4 right-4 px-3 py-2 rounded-xl backdrop-blur-md text-xs font-semibold text-white/90 text-center"
                style={{ background: 'rgba(0,0,0,0.35)' }}
              >
                {current.imageAlt}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Meet the Team - Premium Slider */}
        <section className="space-y-12">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(95,156,76,0.1)] border border-[rgba(95,156,76,0.2)] text-[var(--color-accent)] text-xs font-bold mb-4">
              <Users className="w-3 h-3" /> OUR TEAM
            </div>
            <h2 className="text-[clamp(1.5rem,3.5vw,2.5rem)] font-black text-[var(--color-text)] tracking-tight mb-3">
              <span className="inline-block opacity-0 animate-slide-left-fade" style={{ animationDelay: '0.2s' }}>Meet the</span>&nbsp;
              <span className="inline-block opacity-0 animate-slide-right-fade" style={{ color: 'var(--color-accent)', animationDelay: '0.4s' }}> Experts</span>
            </h2>
            <p className="text-[var(--color-muted)] text-base">Seasoned professionals dedicated to your financial growth and trading success.</p>
          </div>

          {/* Premium Team Slider */}
          <div className="relative max-w-4xl mx-auto pb-10">
            <div className="relative overflow-hidden rounded-3xl">
              <motion.div
                className="flex transition-all duration-700 ease-in-out"
                animate={{ x: `-${currentIndex * 100}%` }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {TEAM.map((member, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-2">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      className="rounded-2xl p-6 sm:p-8 text-center relative overflow-hidden
                        bg-white border border-[#e1ff01]
                        hover:border-[var(--color-accent)] transition-all duration-500"
                    >
                      {/* Background decoration */}
                      <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-accent)] via-transparent to-[var(--color-blue)] opacity-5 group-hover:opacity-10 transition-opacity duration-500"></div>

                      {/* Team member content */}
                      <div className="relative z-10">
                        {/* Avatar placeholder with premium styling */}
                        <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 rounded-xl bg-gradient-to-br from-[var(--color-accent)] to-[var(--color-blue)] p-1 group-hover:scale-105 transition-transform duration-500">
                          <div className="w-full h-full rounded-xl bg-white flex items-center justify-center">
                            <span className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-[var(--color-accent)] to-[var(--color-blue)] bg-clip-text text-transparent">
                              {member.name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                        </div>

                        {/* Team member info */}
                        <motion.h3
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                          className="font-black text-[var(--color-text)] text-lg sm:text-xl mb-2"
                        >
                          {member.name}
                        </motion.h3>

                        <motion.p
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                          className="text-xs sm:text-sm font-bold uppercase tracking-widest mb-3"
                          style={{ color: 'var(--color-accent)' }}
                        >
                          {member.role}
                        </motion.p>

                        {/* Animated underline */}
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: "50px" }}
                          transition={{ delay: 0.5, duration: 0.8 }}
                          className="h-1 mx-auto rounded-full"
                          style={{ background: 'var(--color-accent)' }}
                        />
                      </div>

                      {/* Floating elements */}
                      <motion.div
                        animate={{
                          y: [0, -8, 0],
                          opacity: [0.3, 0.6, 0.3]
                        }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute top-3 right-3 w-2 h-2 rounded-full"
                        style={{ backgroundColor: 'var(--color-accent)' }}
                      />
                      <motion.div
                        animate={{
                          y: [0, 8, 0],
                          opacity: [0.2, 0.5, 0.2]
                        }}
                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                        className="absolute bottom-3 left-3 w-2 h-2 rounded-full"
                        style={{ backgroundColor: 'var(--color-blue)' }}
                      />
                    </motion.div>
                  </div>
                ))}
              </motion.div>
            </div>           
          </div>
        </section>

      </motion.div>
    </div>
  );
}
